import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ENTITY_TYPES, getFormattedAddress } from '@sales-center/common'
import type { ILeadFormModel } from '../typings'
import { useGetLocationOnMount } from '@user/store/locations'
import { useCreateOppActions } from '@user/components/Location'
import {
  useCustomFieldsVisible,
  useSelectCustomFieldData,
} from '@user/store/customFields'
import { useGetAddLeadDataOnMount } from './useGetAddLeadDataOnMount'
import {
  formatInitialPhoneNumberValue,
  getInitialValues,
  getInitialValuesForLocation,
} from '../initialValues'
import { useGetEntityFormDataOnMount } from '@user/components/Settings/EntityFormSettings/hooks'
import { selectAllTargetPests } from '@user/store/targetPests'
import {
  createLocationPermission,
  LocationCreatePermOps,
  selectCurrentUser,
  useCheckPermissions,
} from '@user/store/authorization'
import { activeSources, selectAllSources } from '@user/store/sources/slice'
import {
  activeCampaigns,
  selectAllCampaigns,
} from '@user/store/campaigns/slice'
import {
  addLeadNote,
  addLeadOtherSection,
  addLeadToPestPac,
  entityCampaignId,
  entityLeadSourceId,
  entityNextActionDate,
} from '@user/components/Settings/EntityFormSettings'
import { NoteCode } from '@user/components/Notes/typings'
import { useGetEmployeeListOnMount } from '@user/store/employee/hooks'
import { selectAllEmployees } from '@user/store/employee/slice'
import { guestRoleName } from '@user/components/Settings/Users/UpdateUser/constants'
import { HereMapClickEvent } from '@user/components/HereMap/HereMapNew/hooks/useHereMapInstance'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import { useAppDispatch } from '@user/store/hooks'

type Props = {
  noteCodes: NoteCode[]
  selectedMapLocation?: HereMapClickEvent | null
  fromMapView?: boolean
  draftId?: string | null
}

export function useData({
  noteCodes,
  selectedMapLocation,
  fromMapView = false,
  draftId,
}: Props) {
  const [hideOptional, setHideOptional] = useState(true)

  const createOppActions = useCreateOppActions(true)
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const location = useLocation()
  const leadSources = useSelector(selectAllSources)
  const campaigns = useSelector(selectAllCampaigns)
  const employees = useSelector(selectAllEmployees)
  const currentUser = useSelector(selectCurrentUser)
  const dispatch = useAppDispatch()

  const { settingsBySection } = useGetEntityFormDataOnMount(
    ENTITY_TYPES.Lead,
    hideOptional,
  )

  const { draftIdParam, locationIdParam, phoneParam } = useMemo(() => {
    // When opened from the Map View, the page URL still carries the params of
    // whatever lead/draft was last viewed (e.g. a reviewed pending lead). Those
    // must not leak into the map "Add Lead" form, which should be blank except
    // for the clicked address.
    if (fromMapView) {
      // draftId prop is set when reviewing a pending lead inline from the map
      return {
        phoneParam: null,
        draftIdParam: draftId ?? null,
        locationIdParam: null,
      }
    }

    const params = new URLSearchParams(location.search)

    return {
      phoneParam: params.get('phone'),
      draftIdParam: params.get('draftId'),
      locationIdParam: params.get('locationId'),
    }
  }, [location.search, fromMapView, draftId])

  const employeeLoaded = useGetEmployeeListOnMount(undefined, !!draftIdParam)

  const { loading: dataLoading, draft: draftLead } =
    useGetAddLeadDataOnMount(draftIdParam)

  const locationId = draftLead?.locationId ?? locationIdParam
  const isDraftLead = !!draftLead
  const isAddFromLocation = locationId && !isDraftLead
  const pestPacPinLocationId =
    fromMapView && !draftIdParam
      ? (selectedMapLocation?.pestPacDetails?.locationId ?? null)
      : null
  const { serviceLocation, loadingStatus } = useGetLocationOnMount(
    locationId ?? pestPacPinLocationId,
  )
  const loading = dataLoading || loadingStatus || employeeLoaded === 'pending'
  const { customFields } = useSelectCustomFieldData()
  const allTargetPests = useSelector(selectAllTargetPests)

  const {
    requiredEntitySettings,
    customFieldSettings,
    entitySettingsMap,
    settings,
  } = useGetEntityFormDataOnMount(ENTITY_TYPES.Lead)

  const [canCreateLocations] = useCheckPermissions(
    createLocationPermission as LocationCreatePermOps,
  )

  const guestUser = currentUser?.roleName === guestRoleName
  const mappedGuestUser = guestUser && Boolean(currentUser?.externalUserId)
  const unMappedGuestUser = guestUser && !mappedGuestUser

  const isPestPacPrefill = Boolean(selectedMapLocation?.pestPacDetails)

  const { active: activeAddToPestPac, options: addLocationToPestPac } =
    useMemo(() => {
      const setting = settings.find((x) => x.fieldId === addLeadToPestPac) || {
        active: false,
        options: '',
      }
      const active =
        setting.active &&
        !locationId &&
        !isPestPacPrefill &&
        (mappedGuestUser || canCreateLocations)
      return {
        active: unMappedGuestUser ? false : active,
        options: unMappedGuestUser
          ? false
          : setting.options !== '0' && setting.options !== '',
      }
    }, [
      settings,
      locationId,
      isPestPacPrefill,
      mappedGuestUser,
      canCreateLocations,
      unMappedGuestUser,
    ])

  let initialValues = useMemo<ILeadFormModel>(() => {
    const phoneFormated = formatInitialPhoneNumberValue(phoneParam)
    if (locationId && !draftLead) {
      return getInitialValuesForLocation(
        customFields,
        serviceLocation,
        phoneFormated,
        noteCodes,
      )
    } else {
      return getInitialValues(
        customFields,
        draftLead,
        allTargetPests,
        serviceLocation,
        phoneFormated,
        noteCodes,
        employees,
        addLocationToPestPac,
      )
    }
  }, [
    noteCodes,
    locationId,
    draftLead,
    customFields,
    serviceLocation,
    phoneParam,
    allTargetPests,
    employees,
    addLocationToPestPac,
  ])

  if (selectedMapLocation) {
    const mapAddress1 =
      selectedMapLocation.houseNumber && selectedMapLocation.street
        ? `${selectedMapLocation.houseNumber} ${selectedMapLocation.street}`
        : selectedMapLocation.street || ''
    const mapAddress2 = selectedMapLocation.address?.street2 || ''
    const mapFormattedAddress = getFormattedAddress(
      mapAddress1,
      mapAddress2,
      selectedMapLocation.city || '',
      selectedMapLocation.state || '',
      selectedMapLocation.postalCode || '',
    )
    const pestPacEmails = (selectedMapLocation.pestPacDetails?.email || '')
      .split(/[,;]/)
      .map((e) => e.trim())
      .filter(Boolean)

    initialValues = {
      ...initialValues,
      ...(selectedMapLocation && {
        address1: mapAddress1,
        address2: mapAddress2,
        city: selectedMapLocation.city || '',
        state: selectedMapLocation.state || '',
        postalCode: selectedMapLocation.postalCode || '',
        county: selectedMapLocation.county || '',
        lat: selectedMapLocation.latitude || 0,
        lng: selectedMapLocation.longitude || 0,
        latitude: selectedMapLocation.latitude || 0,
        longitude: selectedMapLocation.longitude || 0,
        fullAddress: selectedMapLocation.label || '',
        contactLocationId:
          selectedMapLocation.contactLocationId &&
          selectedMapLocation.contactLocationId > 0
            ? selectedMapLocation.contactLocationId
            : undefined,
        knockStatusLocationId:
          selectedMapLocation.knockStatusLocationId ?? undefined,
      }),
      ...(selectedMapLocation.pestPacDetails && {
        firstName: selectedMapLocation.pestPacDetails.firstName || '',
        lastName: selectedMapLocation.pestPacDetails.lastName || '',
        company: selectedMapLocation.pestPacDetails.company || '',
        phoneNumber:
          formatInitialPhoneNumberValue(
            selectedMapLocation.pestPacDetails.phone,
          ) || '',
        altPhoneNumber:
          formatInitialPhoneNumberValue(
            selectedMapLocation.pestPacDetails.altPhone,
          ) || '',
        phoneExt: formatInitialPhoneNumberValue(
          selectedMapLocation.pestPacDetails.phone,
        )
          ? selectedMapLocation.pestPacDetails.phoneExt || ''
          : '',
        altPhoneExt: selectedMapLocation.pestPacDetails.altPhoneExt || '',
        emailAddresses: pestPacEmails.length
          ? pestPacEmails.map((e, index) => ({
              emailAddress: e,
              isPrimary: index === 0,
            }))
          : [{ emailAddress: '', isPrimary: true }],
        externalLocationId: selectedMapLocation.pestPacDetails.locationId || '',
        externalLocationCode:
          selectedMapLocation.pestPacDetails.locationCode || '',
        division: selectedMapLocation.pestPacDetails.division || '',
        divisionId:
          selectedMapLocation.pestPacDetails.divisionId != null
            ? String(selectedMapLocation.pestPacDetails.divisionId)
            : '',
        locationType: selectedMapLocation.pestPacDetails.locationType || '',
        locationTypeId:
          selectedMapLocation.pestPacDetails.locationTypeId != null
            ? String(selectedMapLocation.pestPacDetails.locationTypeId)
            : '',
        serviceLocation:
          `${selectedMapLocation.pestPacDetails.locationCode || ''} ${mapFormattedAddress}`.trim(),
        fullAddress: selectedMapLocation.label || mapFormattedAddress,

        addNoteToPestPac: true,
        noteCode: noteCodes?.find((code) => code.isDefault)?.name || '',
        branchId: selectedMapLocation.pestPacDetails.branchId || '',
        branch: selectedMapLocation.pestPacDetails.branch || '',
      }),

      ...(pestPacPinLocationId &&
        serviceLocation && {
          campaignId: serviceLocation.campaignId || '',
          campaign: serviceLocation.campaign || '',
          leadSourceId: serviceLocation.sourceId || '',
          leadSource: serviceLocation.source || '',
        }),
    }
  }

  const [canUpdateLocations] = useCheckPermissions('accounts_update')
  const { externalUserId } = useSelector(selectCurrentUser)

  const isMappedWithUpdateLocationPerm = Boolean(
    canUpdateLocations && externalUserId,
  )

  const isFromPestPacPin = Boolean(pestPacPinLocationId)
  const disabled = Boolean(locationId) || isFromPestPacPin

  const [sourceRequired] = entitySettingsMap.get(entityLeadSourceId) ?? [false]
  const activeLeadSources = useMemo(() => {
    return activeSources(leadSources, sourceRequired)
  }, [leadSources, sourceRequired])

  const [campaignRequired] = entitySettingsMap.get(entityCampaignId) ?? [false]
  const activeLeadCampaigns = useMemo(() => {
    return activeCampaigns(campaigns, campaignRequired)
  }, [campaigns, campaignRequired])

  const { hasEnabledCustomFields, hasRequiredCustomFields } =
    useCustomFieldsVisible()

  const [, showNote] = entitySettingsMap.get(addLeadNote) ?? [false, true]

  const [, showNextActionDate] = entitySettingsMap.get(
    entityNextActionDate,
  ) ?? [false, true]

  const activeOtherSection = useMemo(() => {
    return (
      (!hideOptional &&
        (settingsBySection[addLeadOtherSection].find((x) => x.active) ||
          hasEnabledCustomFields)) ||
      settingsBySection[addLeadOtherSection].find((x) => x.required) ||
      hasRequiredCustomFields ||
      showNote ||
      showNextActionDate
    )
  }, [
    hideOptional,
    settingsBySection,
    hasEnabledCustomFields,
    hasRequiredCustomFields,
    showNote,
    showNextActionDate,
  ])

  const expressView = useMemo(() => {
    return userGeneralPreferences?.find(
      (row) => row?.type == IUserPreferenceGeneralListItemType.ExpressView,
    )
  }, [userGeneralPreferences])

  useEffect(() => {
    if (expressView) {
      setHideOptional(expressView?.value === 'true')
    }
  }, [expressView])

  useEffect(() => {
    if (selectedMapLocation) {
      const mapAddressCache = {
        street1:
          selectedMapLocation.houseNumber && selectedMapLocation.street
            ? `${selectedMapLocation.houseNumber} ${selectedMapLocation.street}`
            : selectedMapLocation.street || '',
        street2: selectedMapLocation.address?.street2 || '',
        city: selectedMapLocation.city || '',
        stateAbbreviation: selectedMapLocation.state || '',
        postalCode: selectedMapLocation.postalCode || '',
        latitude: selectedMapLocation.latitude,
        longitude: selectedMapLocation.longitude,
      }
      sessionStorage.setItem('mapAddressCache', JSON.stringify(mapAddressCache))
    }
  }, [selectedMapLocation])

  const setHideOptionalOnChange = useCallback(
    (value: boolean) => {
      setHideOptional(value)
      dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.ExpressView,
          value: String(value),
        }),
      )
    },
    [dispatch],
  )

  return {
    activeLeadSources,
    activeLeadCampaigns,
    activeOtherSection,
    activeAddToPestPac,
    loading,
    disabled,
    isMappedWithUpdateLocationPerm,
    isDraftLead,
    customFields,
    initialValues,
    createOppActions,
    entitySettingsMap,
    isAddFromLocation,
    isFromPestPacPin,
    customFieldSettings,
    requiredEntitySettings,
    draftLead,
    hideOptional,
    addLocationToPestPac,
    setHideOptional: setHideOptionalOnChange,
  }
}

export type UseData = ReturnType<typeof useData>
